import React, { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import CustomPopover from '@components/CustomPopover'
import { AlertFrequencyType } from '@modules/alerts/types'
import { FormControl, InputLabel, MenuItem, SelectChangeEvent, Typography } from '@mui/material'
import { colors } from '@theme/variables'

import { EditAlertButtonWrapper, FrequencySelect, ProfileAlertPopoverWrapper, StyledProfileAlertButton } from '../index.styles'

interface ProfileAlertPopoverProps {
  onSave: (frequency?: AlertFrequencyType) => void
  onDelete?: () => void
  onCancel?: () => void
  isEdit: boolean
  preselectedAlertFrequency?: AlertFrequencyType
  anchor: Nullable<HTMLElement>
}

const ProfileAlertPopover = ({
  onSave,
  onCancel,
  anchor,
  isEdit,
  preselectedAlertFrequency = AlertFrequencyType.NO_NOTIFICATION,
  onDelete,
}: ProfileAlertPopoverProps) => {
  const { t } = useTranslation()
  const [selectedAlertFrequency, setSelectedAlertFrequncy] = useState<AlertFrequencyType>(
    preselectedAlertFrequency || AlertFrequencyType.NO_NOTIFICATION,
  )
  const [showConfirmPopup, setShowConfirmPopup] = useState(false)

  const handleClose = useCallback(() => {
    if (selectedAlertFrequency !== preselectedAlertFrequency) {
      setShowConfirmPopup(true)
    } else {
      onCancel?.()
    }
  }, [selectedAlertFrequency, preselectedAlertFrequency])

  const title = useMemo(
    () => (isEdit ? t(translationKeys.EDIT_EMAIL_ALERTS_TITLE) : t(translationKeys.ADD_EMAIL_ALERTS_TITLE)),
    [isEdit],
  )
  const selectTitle = useMemo(
    () => (isEdit ? t(translationKeys.EDIT_EMAIL_ALERTS_SUBTITLE) : t(translationKeys.ADD_EMAIL_ALERTS_TITLE)),
    [isEdit],
  )

  return (
    <>
      <CustomPopover
        sx={{ marginTop: '10px' }}
        id="profile-alert-set-notification"
        anchor={anchor}
        setAnchor={handleClose}
        onClose={handleClose}
      >
        <ProfileAlertPopoverWrapper>
          <div>
            <Typography variant="h2">{title}</Typography>
          </div>
          <FormControl variant="outlined" style={{ width: '100%' }}>
            <InputLabel
              sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
              id="profile-alert-select"
            >
              {selectTitle}
            </InputLabel>
            <FrequencySelect
              id="profile-alert-select"
              value={selectedAlertFrequency}
              labelId="profile-alert-select"
              onChange={(e: SelectChangeEvent<AlertFrequencyType>) =>
                setSelectedAlertFrequncy(e.target.value as AlertFrequencyType)
              }
            >
              <MenuItem value={AlertFrequencyType.NO_NOTIFICATION}>{t(translationKeys.ALERT_FREQUENCY_NO_NOTIFICATION)}</MenuItem>
              <MenuItem value={AlertFrequencyType.DAILY}>{t(translationKeys.ALERT_FREQUENCY_DAILY)}</MenuItem>
              <MenuItem value={AlertFrequencyType.WEEKLY}>{t(translationKeys.ALERT_FREQUENCY_WEEKLY)}</MenuItem>
            </FrequencySelect>
          </FormControl>
          <EditAlertButtonWrapper>
            {isEdit ? (
              <StyledProfileAlertButton onClick={() => onDelete?.()} variant="outlined" size="small">
                {t(translationKeys.REMOVE_ALL)}
              </StyledProfileAlertButton>
            ) : (
              <StyledProfileAlertButton onClick={handleClose} variant="outlined" size="small">
                {t(translationKeys.CANCEL_BUTTON)}
              </StyledProfileAlertButton>
            )}
            <StyledProfileAlertButton onClick={() => onSave(selectedAlertFrequency)} variant="contained" size="small">
              {t(translationKeys.SAVE)}
            </StyledProfileAlertButton>
          </EditAlertButtonWrapper>
        </ProfileAlertPopoverWrapper>
      </CustomPopover>
      {showConfirmPopup && (
        <ConfirmPopup
          id="profile-alert-confirm-exit-popup"
          width={294}
          contentWidth={180}
          isOpen
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CONTINUE_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setShowConfirmPopup(false)}
          onConfirm={() => {
            setShowConfirmPopup(false)
            onCancel?.()
          }}
        />
      )}
    </>
  )
}

export default memo(ProfileAlertPopover)
